/**
 * Demo bootstrap — populates the memory storage with deterministic
 * demo data on first load. Runs once per session.
 *
 * This lives in the APPLICATION layer intentionally: it composes ports
 * and engines to build a plausible initial state. The domain itself
 * knows nothing about "demo mode".
 *
 * When a real storage adapter (Mongo/PG/Supabase) is in use, replace this
 * with a real backfill migration or leave it as a no-op.
 */
import { container } from "./container";
import { createActor, createSignal, createClosure } from "../domain/entities";

// Deterministic PRNG so refresh doesn't shuffle numbers.
function mulberry32(seed) {
  return function () {
    let t = (seed += 0x6d2b79f5);
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}
const rand = mulberry32(20260207);
const pick = (arr) => arr[Math.floor(rand() * arr.length)];
const between = (a, b) => Math.floor(rand() * (b - a + 1)) + a;

const COMPANY_ROOTS = [
  "Grupo Andalucía", "Corporativo Monterrey", "Distribuidora del Valle",
  "Inversiones Cumbre", "Servicios Integrales Bogotá", "Comercializadora Pacífico",
  "Tecnología del Sur", "Industrial Aztlán", "Consultora Cordillera",
  "Grupo Empresarial Caribe", "Suministros del Norte", "Comercial Pampas",
  "Innovación Guadalajara", "Corporativo Río Plata", "Distribuidora Yucatán",
  "Grupo Patagonia", "Servicios del Bajío", "Industrial Aconcagua",
  "Comercializadora Cundinamarca", "Grupo Sinaí", "Corporativo Altamira",
  "Distribuciones Atacama", "Servicios Iguazú", "Industrial Xalapa",
  "Grupo Empresarial Quito", "Comercial San Andrés", "Corporativo Manzanillo",
];
const COMPANY_SUFFIX = ["S.A.", "S.A.S.", "S. de R.L.", "Ltda.", "Corp."];
const FIRST_NAMES = ["Carlos","María","José","Ana","Luis","Sofía","Miguel","Valentina","Diego","Camila","Andrés","Isabella","Fernando","Lucía"];
const LAST_NAMES = ["Ramírez","González","Hernández","Rodríguez","Martínez","López","Torres","Vargas","Castro","Mendoza"];
const CONTACT_ROLES = ["Director de Compras","CFO","COO","Gerente Comercial","CEO","Head of IT"];
const SEGMENTS = ["Enterprise", "Mid-Market", "SMB", "Corporativo"];
const SIGNAL_LABELS = [
  "Abrió email hace 2d", "Visitó portal de precios", "Descargó documentación",
  "Sin actividad 90+ días", "Renovación vencida", "Consultó a competidor",
];

let seeded = false;

// Aislamiento de pruebas: con REACT_APP_SEED_DEMO_DATA=false, el equipo
// (operadores) se sigue creando — la UI los espera — pero NO se siembran
// los 200 actores demo ni sus oportunidades ni cierres. Así una carga real
// (Ingestion) queda como la única cartera visible, sin mezclarse con datos
// de demostración. No borra ni reemplaza bootstrapDemo — solo lo acorta.
const SEED_DEMO_DATA = process.env.REACT_APP_SEED_DEMO_DATA !== "false";

function seedActors(n) {
  const drafts = [];
  for (let i = 0; i < n; i++) {
    const company = `${pick(COMPANY_ROOTS)} ${pick(COMPANY_SUFFIX)}`;
    const contact = `${pick(FIRST_NAMES)} ${pick(LAST_NAMES)}`;
    const months = between(2, 18);
    const historical = between(1500, 85000);
    // ~1 de cada 3 actores demo es candidato de renovación (cliente activo,
    // no inactivo) — para que el motor Renewal tenga evidencia real que
    // mostrar en el primer arranque, no solo después de una carga real.
    const isRenewalCandidate = rand() < 0.33;
    const daysUntilRenewal = isRenewalCandidate ? between(3, 75) : null;
    drafts.push(createActor({
      vertical: "software_distributors",
      stage: isRenewalCandidate ? "active" : months > 6 ? "inactive" : "active",
      companyName: company,
      contactName: contact,
      contactRole: pick(CONTACT_ROLES),
      email: `${contact.toLowerCase().replace(/[íáéóú]/g, (c) => ({í:"i",á:"a",é:"e",ó:"o",ú:"u"}[c])).replace(/ /g, ".")}@example.com`,
      phone: `+52 ${between(55, 998)} ${between(1000, 9999)} ${between(1000, 9999)}`,
      segment: pick(SEGMENTS),
      historicalRevenue: { amount: historical, currency: "USD" },
      monthlyAverage: { amount: Math.round(historical / between(6, 36)), currency: "USD" },
      lastActivityAt: new Date(Date.now() - months * 30 * 24 * 3600 * 1000).toISOString(),
      source: "demo",
      isDemo: true,
      verticalAttributes: {
        monthsInactive: months,
        ...(daysUntilRenewal != null ? { daysUntilRenewal } : {}),
      },
    }));
  }
  return drafts;
}

export async function bootstrapDemo() {
  if (seeded) return;
  seeded = true;
  const { ports, engines } = container;
  const { storage } = ports;

  // El operador real (marcador de "tú" hasta que exista auth) siempre
  // existe — la UI lo necesita en cualquier contexto.
  await storage.saveTeamMember({
    id: "user-me", name: "Director Comercial", role: "Operador principal",
    monthClosedValue: { amount: 0, currency: "USD" }, monthClosuresCount: 0,
  });

  if (!SEED_DEMO_DATA) return; // cartera limpia — nada ficticio en beta_founder/live

  // Colegas ficticios — SOLO demo interno. Nunca deben aparecer en una
  // sesión beta_founder o de producción (hallazgo de la prueba guiada).
  await storage.saveTeamMember({
    id: "user-maria", name: "María González", role: "Ejecutiva Senior",
    monthClosedValue: { amount: 47200, currency: "USD" }, monthClosuresCount: 3,
  });
  await storage.saveTeamMember({
    id: "user-luis", name: "Luis Torres", role: "Ejecutivo",
    monthClosedValue: { amount: 32401, currency: "USD" }, monthClosuresCount: 2,
  });
  await storage.saveTeamMember({
    id: "user-ana", name: "Ana Martínez", role: "Ejecutiva",
    monthClosedValue: { amount: 18900, currency: "USD" }, monthClosuresCount: 1,
  });

  // Actors + signals
  const actors = seedActors(200);
  for (const draft of actors) {
    await storage.saveActor(draft);
    await storage.saveSignal(createSignal({
      actorId: draft.id, label: pick(SIGNAL_LABELS), weight: "medium",
      type: "demo_signal",
    }));
  }

  // Evaluate all actors with every live engine. Each engine decides for
  // itself whether an actor is a candidate (Renewal skips actors without
  // daysUntilRenewal — see domain/engines/renewal.js).
  const allActors = await storage.listActors();
  for (const a of allActors) {
    await engines.recovery.evaluateActor(a);
    await engines.renewal.evaluateActor(a);
  }

  // Backfill 11 closures so KPIs are non-zero on first load.
  const opps = await storage.listOpportunities({ engineId: "recovery", limit: 1000 });
  for (let i = 0; i < 11; i++) {
    const opp = opps[i * 3]; // spread through the list
    if (!opp) break;
    await storage.updateOpportunity(opp.id, { outcome: "won" });
    const closedValue = Math.round(opp.estimatedValue.value.amount * (0.7 + rand() * 0.35));
    await storage.saveClosure(createClosure({
      actorId: opp.actorId, companyName: opp.companyName, engineId: "recovery",
      closedValue: { amount: closedValue, currency: "USD" },
      closedAt: new Date(Date.now() - between(1, 25) * 24 * 3600 * 1000).toISOString(),
      closedByName: "Director Comercial",
    }));
  }
  const me = await storage.listTeamMembers().then((ms) => ms.find((m) => m.id === "user-me"));
  const meClosures = await storage.listClosures({ limit: 1000 })
    .then((cs) => cs.filter((c) => c.closedBy === "user-me"));
  const meTotal = meClosures.reduce((s, c) => s + c.closedValue.amount, 0);
  await storage.saveTeamMember({
    ...me,
    monthClosedValue: { amount: meTotal, currency: "USD" },
    monthClosuresCount: meClosures.length,
  });
}
